import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useState } from 'react';
import type { IconType } from 'react-icons';
import { BiAnchor, BiCalendar, BiHomeAlt, BiMapPin, BiRuler } from 'react-icons/bi';

import { getServiceId } from '@/api/tokens.helper';
import { savedHomePort } from '@/components/CreateSheet/CreateVesselSheet/vesselFormSchema';
import { Autocomplete } from '@/components/Inputs';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { GeolocationStatus, useGeolocation } from '@/hooks/useGeolocation';
import { useSavedMarinas } from '@/pages/MyMarinas/useSavedMarinas';
import { useServiceProvidersQuery } from '@/store/service-providers/service-providers.store';
import { homePortPoint, homePortStop } from '@/store/vessel-marinas/home-port';
import { useVessels } from '@/store/vessels/vessels.store';
import {
  berthSuggestion,
  marinaSuggestion,
  matchesQuery,
  mergeSuggestions,
  uniqueByName,
  type Suggestion,
} from '@/utils/locationSuggestions';
import {
  distanceHint,
  nearbyHint,
  nearestPlaces,
  rankByDistance,
  type HintPart,
} from '@/utils/placeDistance';
import {
  PLACES_MIN_QUERY_LENGTH,
  searchNearbyMarinas,
  searchPlaces,
  type NearbyPlace,
  type PlaceSuggestion,
} from '@/utils/placesAutocomplete';
import { useDebounce } from '@/utils/use-debounce';

import { LOCATION_OPTIONS } from '../eventWizard.constants';
import type { LocationStop } from '../eventWizard.types';
import s from '../EventWizardModal.module.scss';

import { SortableLocationRow } from './SortableLocationRow';

const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/** The rating has none: its text says where it comes from. The source is a pill instead. */
const HINT_ICONS: Partial<Record<HintPart['kind'], IconType>> = {
  home: BiHomeAlt,
  type: BiAnchor,
  dates: BiCalendar,
  city: BiMapPin,
  distance: BiRuler,
};

/** How many saved marinas the empty box lists, and how many catalog marinas a search asks for. */
const SAVED_COUNT = 5;
const CATALOG_COUNT = 5;

const HOME_PORT: HintPart = { kind: 'home', text: 'Home port' };

/** The facts under a suggested place, each after its icon. */
function PlaceHint({ parts }: { parts: HintPart[] }) {
  return (
    <span className={s.placeHint}>
      {parts.map(({ kind, text }) => {
        // A pill, so a row from My Marinas or the catalog stands out from Google's at a glance.
        if (kind === 'source') return <StatusPill key={kind} label={text} tone="info" />;
        const Icon = HINT_ICONS[kind];
        return (
          <span key={kind} className={s.placeHintPart}>
            {Icon && <Icon aria-hidden="true" />}
            {text}
          </span>
        );
      })}
    </span>
  );
}

const toOption = ({ value, label, parts }: Suggestion) => ({
  value,
  label,
  hint: parts.length > 0 ? <PlaceHint parts={parts} /> : undefined,
});

interface StepLocationProps {
  locationQuery: string;
  onLocationQueryChange: (value: string) => void;
  locations: LocationStop[];
  onAddLocation: (value: string | null, hint?: HintPart[]) => void;
  onRemoveLocation: (id: number) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export function StepLocation({
  locationQuery,
  onLocationQueryChange,
  locations,
  onAddLocation,
  onRemoveLocation,
  onDragEnd,
}: StepLocationProps) {
  const [places, setPlaces] = useState<PlaceSuggestion[]>([]);
  const [nearby, setNearby] = useState<NearbyPlace[]>([]);
  const debouncedQuery = useDebounce(locationQuery);
  const search = debouncedQuery.trim();
  const isSearching = search.length >= PLACES_MIN_QUERY_LENGTH;

  // Not `state.vessel`: `updateVessel` leaves it stale, so a changed home port would not show.
  const vessel = useVessels((state) => state.myVessels?.find((v) => v.id === getServiceId()));
  const homePort = vessel ? savedHomePort(vessel) : undefined;
  const homePoint = homePortPoint(homePort);
  // The device's location only stands in when there is no home port to rank by, and only once
  // the vessel has loaded: one that has a home port never gets the prompt.
  const geo = useGeolocation(Infinity, !!vessel && !homePoint);
  const fix = geo.status === GeolocationStatus.SUCCESS ? geo.coords : undefined;
  const latitude = homePoint?.latitude ?? fix?.latitude;
  const longitude = homePoint?.longitude ?? fix?.longitude;
  const from = homePoint ? 'home port' : 'you';
  const origin =
    latitude !== undefined && longitude !== undefined ? { latitude, longitude } : undefined;

  const { berths, starred, canBrowse } = useSavedMarinas();
  const catalogQuery = useServiceProvidersQuery(
    CATALOG_COUNT,
    { vesselId: getServiceId(), type: 'marina', search },
    // The previous rows stay until the new ones land, as Google's do, so the list does not jump.
    { enabled: canBrowse && isSearching, keepPrevious: true },
  );

  // ponytail: a fix landing while the user types re-runs the query once, so the list can reorder
  // under their finger. Reading the origin only when a query starts is the upgrade if QA trips on it.
  useEffect(() => {
    if (!isSearching) return;

    const controller = new AbortController();
    const near =
      latitude !== undefined && longitude !== undefined ? { latitude, longitude } : undefined;
    searchPlaces(debouncedQuery, controller.signal, near).then((result) => {
      if (!controller.signal.aborted) setPlaces(result);
    });

    return () => controller.abort();
  }, [debouncedQuery, isSearching, latitude, longitude]);

  // ponytail: until the marinas arrive (or if the call fails) the box shows the fixed list.
  useEffect(() => {
    if (latitude === undefined || longitude === undefined) return;

    let live = true;
    searchNearbyMarinas({ latitude, longitude }).then((result) => {
      if (live) setNearby(result);
    });

    return () => {
      live = false;
    };
  }, [latitude, longitude]);

  const saved = [
    ...berths.map((berth) => berthSuggestion(berth, origin, from)),
    ...starred.map((marina) => marinaSuggestion(marina, origin, from, 'saved')),
  ];

  // The saved row stands for the catalog marina it was picked from or starred.
  const savedIds = new Set<string | undefined>([
    ...berths.map((berth) => berth.serviceProviderId),
    ...starred.map((marina) => marina.id),
  ]);
  const catalog = (catalogQuery.data?.pages[0]?.items ?? [])
    .filter((marina) => !savedIds.has(marina.id))
    .map((marina) => marinaSuggestion(marina, origin, from, 'catalog'));
  const google = places.map(({ value, label, distanceMeters }): Suggestion => {
    const distance = distanceHint(distanceMeters, from);
    return { value, label, distanceMeters, parts: distance ? [distance] : [] };
  });
  const searchOptions = mergeSuggestions(
    saved.filter((place) => matchesQuery(place, search)),
    catalog,
    google,
  );

  const homePortOption: Suggestion[] = homePort?.name
    ? [{ value: homePortStop(homePort), label: homePort.name, parts: [HOME_PORT] }]
    : [];
  const nearbyOptions = nearestPlaces(nearby, from).map(
    ({ value, label, ...place }): Suggestion => ({ value, label, parts: nearbyHint(place, from) }),
  );
  const defaults = nearbyOptions.length
    ? nearbyOptions
    : LOCATION_OPTIONS.map((option): Suggestion => ({ ...option, parts: [] }));
  // The home port leads, so the berth it was copied from drops out as the same name.
  const emptyOptions = uniqueByName([
    ...homePortOption,
    ...rankByDistance(saved).slice(0, SAVED_COUNT),
    ...defaults,
  ]);
  const shown = isSearching ? searchOptions : emptyOptions;

  // A stop loaded from the server keeps no facts; the home port and saved marinas are found by name.
  // The home port goes last, so it wins over the berth it was copied from, as in the empty box.
  const known = new Map([...saved, ...homePortOption].map(({ value, parts }) => [value, parts]));

  const locationSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <>
      <div className={s.stepHeader}>
        <h3 className={s.stepTitle}>Where will it take place?</h3>
        <p className={s.stepSubtitle}>Please add one or more locations, or skip for now.</p>
      </div>
      <div className={s.fields}>
        <Autocomplete
          label="Location"
          placeholder="Port Hercule, Monaco"
          value={locationQuery}
          onChange={onLocationQueryChange}
          options={shown.map(toOption)}
          onItemSelect={(value) =>
            onAddLocation(value, shown.find((option) => option.value === value)?.parts)
          }
        />
        {locations.length > 0 && (
          <div className={s.rowList}>
            <div className={s.listCount}>
              {locations.length} location{locations.length !== 1 ? 's' : ''} added
            </div>
            <DndContext
              sensors={locationSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={onDragEnd}
            >
              <SortableContext items={locations} strategy={verticalListSortingStrategy}>
                {locations.map((stop, idx) => {
                  // The name is "name, city" already, so the row leaves the city out.
                  const parts = (stop.hint ?? known.get(stop.name))?.filter(
                    (part) => part.kind !== 'city' || !stop.name.includes(part.text),
                  );
                  return (
                    <SortableLocationRow
                      key={stop.id}
                      id={stop.id}
                      name={stop.name}
                      hint={parts?.length ? <PlaceHint parts={parts} /> : undefined}
                      index={idx}
                      onRemove={() => onRemoveLocation(stop.id)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </>
  );
}
